import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import type { Advertisement, AdvertisementImage } from '../types';
import { authenticatedFetch } from '../utils/api';

interface AdvertisementFormProps {
  editingAd?: Advertisement | null;
  onSave: () => void;
  onCancel: () => void;
}

const AVAILABLE_INDICES = [
  { name: 'asmi-wp-pages', displayName: 'Member & Society Info' },
  { name: 'asmi-wp-products', displayName: 'Products' },
  { name: 'asmi-wp-events', displayName: 'Education Courses' },
  { name: 'asmi-wp-asm-news', displayName: 'Industry News' },
  { name: 'asmi-wp-videos', displayName: 'Videos' },
  { name: 'asmi-dl-books', displayName: 'Digital Books' },
  { name: 'asmi-dl-chapters', displayName: 'Digital Book Chapters' },
  { name: 'asmi-dl-technical_articles', displayName: 'Magazine Articles' },
  { name: 'asmi-dl-proceedings', displayName: 'Conference Papers' },
  { name: 'asmi-dl-datasheets', displayName: 'Alloy Digest Datasheets' },
];

const AVAILABLE_REGIONS = [
  // United States - All 50 states + DC
  { code: 'US', name: 'United States (All)' },
  { code: 'US-AL', name: 'United States - Alabama' },
  { code: 'US-AK', name: 'United States - Alaska' },
  { code: 'US-AZ', name: 'United States - Arizona' },
  { code: 'US-AR', name: 'United States - Arkansas' },
  { code: 'US-CA', name: 'United States - California' },
  { code: 'US-CO', name: 'United States - Colorado' },
  { code: 'US-CT', name: 'United States - Connecticut' },
  { code: 'US-DE', name: 'United States - Delaware' },
  { code: 'US-DC', name: 'United States - District of Columbia' },
  { code: 'US-FL', name: 'United States - Florida' },
  { code: 'US-GA', name: 'United States - Georgia' },
  { code: 'US-HI', name: 'United States - Hawaii' },
  { code: 'US-ID', name: 'United States - Idaho' },
  { code: 'US-IL', name: 'United States - Illinois' },
  { code: 'US-IN', name: 'United States - Indiana' },
  { code: 'US-IA', name: 'United States - Iowa' },
  { code: 'US-KS', name: 'United States - Kansas' },
  { code: 'US-KY', name: 'United States - Kentucky' },
  { code: 'US-LA', name: 'United States - Louisiana' },
  { code: 'US-ME', name: 'United States - Maine' },
  { code: 'US-MD', name: 'United States - Maryland' },
  { code: 'US-MA', name: 'United States - Massachusetts' },
  { code: 'US-MI', name: 'United States - Michigan' },
  { code: 'US-MN', name: 'United States - Minnesota' },
  { code: 'US-MS', name: 'United States - Mississippi' },
  { code: 'US-MO', name: 'United States - Missouri' },
  { code: 'US-MT', name: 'United States - Montana' },
  { code: 'US-NE', name: 'United States - Nebraska' },
  { code: 'US-NV', name: 'United States - Nevada' },
  { code: 'US-NH', name: 'United States - New Hampshire' },
  { code: 'US-NJ', name: 'United States - New Jersey' },
  { code: 'US-NM', name: 'United States - New Mexico' },
  { code: 'US-NY', name: 'United States - New York' },
  { code: 'US-NC', name: 'United States - North Carolina' },
  { code: 'US-ND', name: 'United States - North Dakota' },
  { code: 'US-OH', name: 'United States - Ohio' },
  { code: 'US-OK', name: 'United States - Oklahoma' },
  { code: 'US-OR', name: 'United States - Oregon' },
  { code: 'US-PA', name: 'United States - Pennsylvania' },
  { code: 'US-RI', name: 'United States - Rhode Island' },
  { code: 'US-SC', name: 'United States - South Carolina' },
  { code: 'US-SD', name: 'United States - South Dakota' },
  { code: 'US-TN', name: 'United States - Tennessee' },
  { code: 'US-TX', name: 'United States - Texas' },
  { code: 'US-UT', name: 'United States - Utah' },
  { code: 'US-VT', name: 'United States - Vermont' },
  { code: 'US-VA', name: 'United States - Virginia' },
  { code: 'US-WA', name: 'United States - Washington' },
  { code: 'US-WV', name: 'United States - West Virginia' },
  { code: 'US-WI', name: 'United States - Wisconsin' },
  { code: 'US-WY', name: 'United States - Wyoming' },

  // Canada - All 10 provinces + 3 territories
  { code: 'CA', name: 'Canada (All)' },
  { code: 'CA-AB', name: 'Canada - Alberta' },
  { code: 'CA-BC', name: 'Canada - British Columbia' },
  { code: 'CA-MB', name: 'Canada - Manitoba' },
  { code: 'CA-NB', name: 'Canada - New Brunswick' },
  { code: 'CA-NL', name: 'Canada - Newfoundland and Labrador' },
  { code: 'CA-NS', name: 'Canada - Nova Scotia' },
  { code: 'CA-NT', name: 'Canada - Northwest Territories' },
  { code: 'CA-NU', name: 'Canada - Nunavut' },
  { code: 'CA-ON', name: 'Canada - Ontario' },
  { code: 'CA-PE', name: 'Canada - Prince Edward Island' },
  { code: 'CA-QC', name: 'Canada - Quebec' },
  { code: 'CA-SK', name: 'Canada - Saskatchewan' },
  { code: 'CA-YT', name: 'Canada - Yukon' },

  // Mexico - All 32 states
  { code: 'MX', name: 'Mexico (All)' },
  { code: 'MX-AGU', name: 'Mexico - Aguascalientes' },
  { code: 'MX-BCN', name: 'Mexico - Baja California' },
  { code: 'MX-BCS', name: 'Mexico - Baja California Sur' },
  { code: 'MX-CAM', name: 'Mexico - Campeche' },
  { code: 'MX-CHP', name: 'Mexico - Chiapas' },
  { code: 'MX-CHH', name: 'Mexico - Chihuahua' },
  { code: 'MX-CMX', name: 'Mexico - Mexico City' },
  { code: 'MX-COA', name: 'Mexico - Coahuila' },
  { code: 'MX-COL', name: 'Mexico - Colima' },
  { code: 'MX-DUR', name: 'Mexico - Durango' },
  { code: 'MX-GUA', name: 'Mexico - Guanajuato' },
  { code: 'MX-GRO', name: 'Mexico - Guerrero' },
  { code: 'MX-HID', name: 'Mexico - Hidalgo' },
  { code: 'MX-JAL', name: 'Mexico - Jalisco' },
  { code: 'MX-MEX', name: 'Mexico - Mexico State' },
  { code: 'MX-MIC', name: 'Mexico - Michoacán' },
  { code: 'MX-MOR', name: 'Mexico - Morelos' },
  { code: 'MX-NAY', name: 'Mexico - Nayarit' },
  { code: 'MX-NLE', name: 'Mexico - Nuevo León' },
  { code: 'MX-OAX', name: 'Mexico - Oaxaca' },
  { code: 'MX-PUE', name: 'Mexico - Puebla' },
  { code: 'MX-QUE', name: 'Mexico - Querétaro' },
  { code: 'MX-ROO', name: 'Mexico - Quintana Roo' },
  { code: 'MX-SLP', name: 'Mexico - San Luis Potosí' },
  { code: 'MX-SIN', name: 'Mexico - Sinaloa' },
  { code: 'MX-SON', name: 'Mexico - Sonora' },
  { code: 'MX-TAB', name: 'Mexico - Tabasco' },
  { code: 'MX-TAM', name: 'Mexico - Tamaulipas' },
  { code: 'MX-TLA', name: 'Mexico - Tlaxcala' },
  { code: 'MX-VER', name: 'Mexico - Veracruz' },
  { code: 'MX-YUC', name: 'Mexico - Yucatán' },
  { code: 'MX-ZAC', name: 'Mexico - Zacatecas' },

  // India - All 28 states + 8 union territories
  { code: 'IN', name: 'India (All)' },
  { code: 'IN-AP', name: 'India - Andhra Pradesh' },
  { code: 'IN-AR', name: 'India - Arunachal Pradesh' },
  { code: 'IN-AS', name: 'India - Assam' },
  { code: 'IN-BR', name: 'India - Bihar' },
  { code: 'IN-CT', name: 'India - Chhattisgarh' },
  { code: 'IN-GA', name: 'India - Goa' },
  { code: 'IN-GJ', name: 'India - Gujarat' },
  { code: 'IN-HR', name: 'India - Haryana' },
  { code: 'IN-HP', name: 'India - Himachal Pradesh' },
  { code: 'IN-JH', name: 'India - Jharkhand' },
  { code: 'IN-KA', name: 'India - Karnataka' },
  { code: 'IN-KL', name: 'India - Kerala' },
  { code: 'IN-MP', name: 'India - Madhya Pradesh' },
  { code: 'IN-MH', name: 'India - Maharashtra' },
  { code: 'IN-MN', name: 'India - Manipur' },
  { code: 'IN-ML', name: 'India - Meghalaya' },
  { code: 'IN-MZ', name: 'India - Mizoram' },
  { code: 'IN-NL', name: 'India - Nagaland' },
  { code: 'IN-OR', name: 'India - Odisha' },
  { code: 'IN-PB', name: 'India - Punjab' },
  { code: 'IN-RJ', name: 'India - Rajasthan' },
  { code: 'IN-SK', name: 'India - Sikkim' },
  { code: 'IN-TN', name: 'India - Tamil Nadu' },
  { code: 'IN-TG', name: 'India - Telangana' },
  { code: 'IN-TR', name: 'India - Tripura' },
  { code: 'IN-UP', name: 'India - Uttar Pradesh' },
  { code: 'IN-UT', name: 'India - Uttarakhand' },
  { code: 'IN-WB', name: 'India - West Bengal' },
  { code: 'IN-AN', name: 'India - Andaman and Nicobar Islands' },
  { code: 'IN-CH', name: 'India - Chandigarh' },
  { code: 'IN-DN', name: 'India - Dadra and Nagar Haveli and Daman and Diu' },
  { code: 'IN-DL', name: 'India - Delhi' },
  { code: 'IN-JK', name: 'India - Jammu and Kashmir' },
  { code: 'IN-LA', name: 'India - Ladakh' },
  { code: 'IN-LD', name: 'India - Lakshadweep' },
  { code: 'IN-PY', name: 'India - Puducherry' },

  // United Kingdom
  { code: 'GB', name: 'United Kingdom' },

  // European Union Countries (27 members as of 2024)
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },

  // Other major countries
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'AU', name: 'Australia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SG', name: 'Singapore' },
  { code: 'OTHER', name: 'Other/Worldwide' },
];

const AdvertisementForm: React.FC<AdvertisementFormProps> = ({ editingAd, onSave, onCancel }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [regionSearchTerm, setRegionSearchTerm] = useState('');

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  // Helper to convert relative image URLs to absolute
  const getAbsoluteImageUrl = (url: string | undefined): string => {
    if (!url) return '';
    // If already absolute (starts with http:// or https://), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If relative, prepend API base URL
    return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const [formData, setFormData] = useState<Partial<Advertisement> & { keywordsText?: string; indexesText?: string }>({
    name: '',
    type: 'banner',
    placement: 'right-sidebar',
    size: 'medium',
    sticky: true,
    title: '',
    imageUrl: '', // Temporary field for URL input
    images: [],
    rotationDuration: 5000,
    showAlways: true,
    targetKeywords: [],
    targetIndexes: [],
    targetRegions: [],
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    priority: 1,
    active: true,
  });

  useEffect(() => {
    if (editingAd) {
      // Migrate legacy single image to images array if needed
      let images = editingAd.images || [];
      if (images.length === 0 && editingAd.imageUrl) {
        images = [
          {
            url: editingAd.imageUrl,
            width: editingAd.imageWidth,
            height: editingAd.imageHeight,
            altText: editingAd.altText,
          },
        ];
      }

      setFormData({
        ...editingAd,
        images,
        keywordsText: editingAd.targetKeywords?.join(', ') || '',
        indexesText: editingAd.targetIndexes?.join(', ') || '',
      });
    }
  }, [editingAd]);

  const handleKeywordChange = (text: string) => {
    const keywords = text
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);
    setFormData({ ...formData, keywordsText: text, targetKeywords: keywords });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      // Validate that at least one image has a clickUrl
      const hasAtLeastOneClickUrl = formData.images?.some((img) => img.clickUrl && img.clickUrl.trim() !== '');
      if (!hasAtLeastOneClickUrl) {
        setError('At least one image must have a Click URL. Please expand "Edit details & link" and add a URL.');
        setIsSaving(false);
        return;
      }

      // Validate that all clickUrls have proper protocols
      const invalidUrls = formData.images?.filter((img) => {
        if (!img.clickUrl) return false;
        const url = img.clickUrl.trim();
        return url && !url.startsWith('http://') && !url.startsWith('https://');
      });

      if (invalidUrls && invalidUrls.length > 0) {
        setError('All Click URLs must start with http:// or https://. Please check your image links.');
        setIsSaving(false);
        return;
      }

      // Validate that all images have durations
      const missingDurations = formData.images?.filter((img) => !img.duration || img.duration <= 0);
      if (missingDurations && missingDurations.length > 0) {
        setError('All images must have a Display Duration. Please edit each image and set the duration.');
        setIsSaving(false);
        return;
      }

      // Remove form-only fields before sending to API
      const { keywordsText, indexesText, ...adData } = formData;

      const finalAdData: Partial<Advertisement> = {
        ...adData,
        targetKeywords: formData.targetKeywords || [],
        targetIndexes: formData.targetIndexes || [],
      };

      // Remove placement field for modal ads
      if (finalAdData.type === 'modal') {
        delete finalAdData.placement;
      }

      const url = editingAd
        ? `${API_BASE_URL}/api/advertisements/${editingAd.id}`
        : `${API_BASE_URL}/api/advertisements`;

      const method = editingAd ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalAdData),
      });

      if (!response.ok) throw new Error('Failed to save advertisement');

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTypeChange = (type: 'banner' | 'modal') => {
    const updates: Partial<Advertisement> = { type };

    // Clear placement and sticky for modal ads
    if (type === 'modal') {
      updates.placement = undefined;
      updates.sticky = undefined;
    } else {
      updates.placement = formData.placement || 'right-sidebar';
      updates.sticky = formData.sticky !== undefined ? formData.sticky : true;
    }

    setFormData({ ...formData, ...updates });
  };

  const handlePlacementChange = (placement: Advertisement['placement']) => {
    setFormData({ ...formData, placement });
  };

  const toggleIndex = (indexName: string) => {
    const currentIndexes = formData.targetIndexes || [];
    const newIndexes = currentIndexes.includes(indexName)
      ? currentIndexes.filter((i) => i !== indexName)
      : [...currentIndexes, indexName];
    setFormData({ ...formData, targetIndexes: newIndexes });
  };

  const toggleRegion = (regionCode: string) => {
    const currentRegions = formData.targetRegions || [];

    // If toggling "OTHER/Worldwide"
    if (regionCode === 'OTHER') {
      // If OTHER is already selected, deselect it
      if (currentRegions.includes('OTHER')) {
        setFormData({ ...formData, targetRegions: [] });
      } else {
        // If OTHER is being selected, replace all regions with just OTHER
        setFormData({ ...formData, targetRegions: ['OTHER'] });
      }
      return;
    }

    // If selecting any other region while OTHER is selected, remove OTHER
    const regionsWithoutOther = currentRegions.filter((r) => r !== 'OTHER');

    // Handle "(All)" regions - select/deselect all child regions
    if (regionCode === 'US' || regionCode === 'CA' || regionCode === 'MX' || regionCode === 'IN') {
      // Get all child regions for this country
      const childRegions = AVAILABLE_REGIONS.filter((r) => r.code.startsWith(regionCode + '-')).map((r) => r.code);

      // Check if all child regions are currently selected
      const allSelected = childRegions.every((child) => regionsWithoutOther.includes(child));

      if (allSelected) {
        // Deselect all child regions and the parent
        const newRegions = regionsWithoutOther.filter((r) => !r.startsWith(regionCode));
        setFormData({ ...formData, targetRegions: newRegions });
      } else {
        // Select all child regions (don't include parent "All" itself)
        const otherRegions = regionsWithoutOther.filter((r) => !r.startsWith(regionCode));
        setFormData({ ...formData, targetRegions: [...otherRegions, ...childRegions] });
      }
      return;
    }

    // Toggle the selected region
    const newRegions = regionsWithoutOther.includes(regionCode)
      ? regionsWithoutOther.filter((r) => r !== regionCode)
      : [...regionsWithoutOther, regionCode];

    setFormData({ ...formData, targetRegions: newRegions });
  };

  // Helper function to compress image if needed
  const compressImage = async (file: File, maxSizeMB: number = 4): Promise<File> => {
    // If file is already small enough, return it
    if (file.size <= maxSizeMB * 1024 * 1024) {
      return file;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions to reduce file size
          const maxDimension = 2000;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Try different quality levels to get under the size limit
          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to compress image'));
                  return;
                }

                // If still too large and we can reduce quality more, try again
                if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.5) {
                  quality -= 0.1;
                  tryCompress();
                  return;
                }

                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              },
              'image/jpeg',
              quality
            );
          };

          tryCompress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const addImage = async (file?: File, url?: string) => {
    if (file) {
      // Upload file
      setUploadingImage(true);
      setError(null);

      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error('Please select an image file');
        }

        // Check file size and compress if needed
        const maxSize = 5 * 1024 * 1024; // 5MB
        let fileToUpload = file;

        if (file.size > maxSize) {
          setError(`Compressing image (${(file.size / 1024 / 1024).toFixed(1)}MB)...`);
          try {
            fileToUpload = await compressImage(file, 4); // Compress to ~4MB to have buffer
            setError(null);
          } catch (compressErr) {
            throw new Error('Failed to compress image. Please use a smaller file.');
          }
        }

        const uploadFormData = new FormData();
        uploadFormData.append('file', fileToUpload);

        const response = await fetch(`${API_BASE_URL}/api/upload-image`, {
          method: 'POST',
          body: uploadFormData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Upload failed with status ${response.status}`);
        }

        const data = await response.json();
        url = data.url;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload image');
        setUploadingImage(false);
        return;
      } finally {
        setUploadingImage(false);
      }
    }

    if (url) {
      // Determine default dimensions based on ad type and placement
      let defaultWidth = 300;
      let defaultHeight = 600;

      if (formData.type === 'modal') {
        defaultWidth = 600;
        defaultHeight = 400;
      } else if (formData.placement === 'left-sidebar' || formData.placement === 'right-sidebar') {
        defaultWidth = 300;
        defaultHeight = 600;
      } else if (formData.placement === 'top' || formData.placement === 'bottom') {
        defaultWidth = 728;
        defaultHeight = 90;
      }

      const newImage: AdvertisementImage = {
        url,
        width: defaultWidth,
        height: defaultHeight,
        altText: '',
      };

      // Use functional state update to ensure we have the latest state
      setFormData((prevData) => ({
        ...prevData,
        images: [...(prevData.images || []), newImage],
      }));
    }
  };

  const removeImage = (index: number) => {
    const currentImages = formData.images || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const updateImage = (index: number, updates: Partial<AdvertisementImage>) => {
    const currentImages = formData.images || [];
    const newImages = currentImages.map((img, i) => (i === index ? { ...img, ...updates } : img));
    setFormData({ ...formData, images: newImages });
  };

  const validateAndCloseModal = () => {
    if (editingImageIndex === null || !formData.images) return;

    const image = formData.images[editingImageIndex];

    // Validate required fields
    if (!image.clickUrl || image.clickUrl.trim() === '') {
      setModalError('Click URL is required');
      return;
    }

    // Validate URL has protocol
    const url = image.clickUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setModalError('Click URL must start with http:// or https://');
      return;
    }

    // Validate duration
    if (!image.duration || image.duration <= 0) {
      setModalError('Display Duration is required and must be greater than 0');
      return;
    }

    // All validations passed, close modal
    setModalError(null);
    setEditingImageIndex(null);
  };

  return (
    <div className="mx-auto p-8 cursor-pointer">
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-zinc-200 cursor-pointer">
        <h3 className="text-2xl font-semibold text-zinc-900 cursor-pointer">
          {editingAd ? 'Edit Advertisement' : 'Create New Advertisement'}
        </h3>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 rounded-lg text-sm font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 hover:border-zinc-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            className="px-6 py-2 rounded-lg text-sm font-medium bg-zinc-900 text-white border border-zinc-900 hover:bg-zinc-800 hover:border-zinc-800 transition-colors disabled:bg-zinc-400 disabled:border-zinc-400 disabled:cursor-not-allowed cursor-pointer"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingAd ? 'Update Advertisement' : 'Create Advertisement'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 text-2xl text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 rounded transition-colors leading-none cursor-pointer"
          >
            ✕
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 mb-4 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm cursor-pointer">
          {error}
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-8 cursor-pointer">
        {/* Basic Info */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 cursor-pointer">
          <h4 className="text-base font-semibold text-zinc-900 mb-5 pb-3 border-b border-zinc-100 cursor-pointer">
            Basic Information
          </h4>

          <div className="mb-5 cursor-pointer">
            <label htmlFor="name" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
              Internal Name <span className="text-red-600 cursor-pointer">*</span>
              <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">For your reference only</small>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Steel Products Banner - Q1 2025"
              className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4 cursor-pointer">
            <div className="mb-5 cursor-pointer">
              <label htmlFor="type" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                Advertisement Type <span className="text-red-600 cursor-pointer">*</span>
              </label>
              <select
                id="type"
                value={formData.type || 'banner'}
                onChange={(e) => handleTypeChange(e.target.value as 'banner' | 'modal')}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                required
              >
                <option value="banner">Banner Ad</option>
                <option value="modal">Modal Popup</option>
              </select>
            </div>

            {formData.type === 'banner' && (
              <>
                <div className="mb-5 cursor-pointer">
                  <label htmlFor="placement" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                    Placement <span className="text-red-600 cursor-pointer">*</span>
                  </label>
                  <select
                    id="placement"
                    value={formData.placement || 'right-sidebar'}
                    onChange={(e) => handlePlacementChange(e.target.value as Advertisement['placement'])}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                    required
                  >
                    <option value="left-sidebar">Left Sidebar</option>
                    <option value="right-sidebar">Right Sidebar</option>
                    <option value="top">Top Banner</option>
                    <option value="bottom">Bottom Banner</option>
                  </select>
                </div>

                <div className="mb-5 cursor-pointer">
                  <label htmlFor="size" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                    Banner Size <span className="text-red-600 cursor-pointer">*</span>
                  </label>
                  <select
                    id="size"
                    value={formData.size || 'medium'}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value as 'small' | 'medium' | 'large' })}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                    required
                  >
                    {(formData.placement === 'left-sidebar' || formData.placement === 'right-sidebar') ? (
                      <>
                        <option value="small">Small (250x250)</option>
                        <option value="medium">Medium (300x600)</option>
                        <option value="large">Large (300x1050)</option>
                      </>
                    ) : (
                      <>
                        <option value="small">Small (728x90)</option>
                        <option value="medium">Medium (970x90)</option>
                        <option value="large">Large (970x250)</option>
                      </>
                    )}
                  </select>
                </div>

                {(formData.placement === 'left-sidebar' || formData.placement === 'right-sidebar') && (
                  <div className="mb-5 cursor-pointer">
                    <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sticky || false}
                        onChange={(e) => setFormData({ ...formData, sticky: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                      />
                      Sticky (stays visible while scrolling)
                    </label>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Creative Content */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 cursor-pointer">
          <h4 className="text-base font-semibold text-zinc-900 mb-5 pb-3 border-b border-zinc-100 cursor-pointer">
            Creative Content
          </h4>

          <div className="mb-5 cursor-pointer">
            <label htmlFor="title" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
              Title
              <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">Optional headline text</small>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Optional ad title"
              className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            />
          </div>

          <div className="mb-5 cursor-pointer">
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hideTitle || false}
                onChange={(e) => setFormData({ ...formData, hideTitle: e.target.checked })}
                className="w-4 h-4 cursor-pointer"
              />
              Hide title in modal
            </label>
          </div>

          <div className="mb-5 cursor-pointer">
            <label className="block mb-3 cursor-pointer">
              Images <span className="text-red-600 cursor-pointer">*</span>
              <small className="block mt-1 text-zinc-500 cursor-pointer">
                Add one or more images. Multiple images will rotate automatically. Set dimensions for each image
                individually.
              </small>
            </label>

            {/* Images Grid */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-4 cursor-pointer">
              {formData.images?.map((img, index) => (
                <div
                  key={index}
                  className="relative border-2 border-zinc-200 rounded-lg p-2 bg-zinc-50 transition-all hover:border-indigo-500 cursor-pointer"
                >
                  {/* Image Number Badge */}
                  <div className="absolute top-2 left-2 bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold z-10 cursor-pointer">
                    {index + 1}
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white border-none rounded w-6 h-6 flex items-center justify-center cursor-pointer text-base leading-none z-10 hover:bg-red-600 cursor-pointer"
                    title="Remove image"
                  >
                    ×
                  </button>

                  {/* Image Preview */}
                  <div className="w-full h-[150px] flex items-center justify-center bg-white rounded mb-2 overflow-hidden cursor-pointer">
                    <img
                      src={getAbsoluteImageUrl(img.url)}
                      alt={img.altText || `Image ${index + 1}`}
                      className="max-w-full max-h-full object-contain cursor-pointer"
                    />
                  </div>

                  {/* Image Info */}
                  <div className="text-xs text-zinc-500 mb-2 text-center cursor-pointer">
                    {img.clickUrl ? '✓ Configured' : '⚠ Needs setup'}
                  </div>

                  {/* Edit Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setModalError(null);
                      setEditingImageIndex(index);
                    }}
                    className="w-full px-2 py-2 bg-indigo-500 text-white border-none rounded cursor-pointer text-sm font-medium hover:bg-indigo-600 cursor-pointer"
                  >
                    Edit Details
                  </button>
                </div>
              ))}

              {/* Add Image Card */}
              <div className="border-2 border-dashed border-zinc-300 rounded-lg p-4 flex flex-col items-center justify-center min-h-[200px] bg-zinc-50 cursor-pointer">
                <div className="text-4xl text-zinc-400 mb-2 cursor-pointer">+</div>
                <div className="text-sm text-zinc-600 mb-4 text-center cursor-pointer">Add Image</div>

                {/* Upload Button */}
                <label className="px-4 py-2 bg-indigo-500 text-white rounded-md cursor-pointer text-sm font-medium mb-2 inline-block hover:bg-indigo-600 cursor-pointer">
                  {uploadingImage ? 'Uploading...' : 'Upload File'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        addImage(file);
                        e.target.value = '';
                      }
                    }}
                    disabled={uploadingImage}
                    className="hidden cursor-pointer"
                  />
                </label>

                {/* URL Input */}
                <div className="text-xs text-zinc-400 my-2 cursor-pointer">or</div>
                <div className="flex gap-2 w-full cursor-pointer">
                  <input
                    type="url"
                    value={formData.imageUrl || ''}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="Image URL"
                    className="flex-1 min-w-0 px-2 py-2 text-sm border border-zinc-300 rounded cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (formData.imageUrl) {
                        const url = formData.imageUrl;
                        // Add the image first
                        await addImage(undefined, url);
                        // Then clear the input using functional update
                        setFormData((prev) => ({ ...prev, imageUrl: '' }));
                      }
                    }}
                    disabled={!formData.imageUrl || uploadingImage}
                    className={`px-3 py-2 text-white border-none rounded text-sm font-medium shrink-0 ${formData.imageUrl ? 'bg-emerald-500 cursor-pointer hover:bg-emerald-600' : 'bg-zinc-300 cursor-not-allowed'}`}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {(!formData.images || formData.images.length === 0) && (
              <small className="text-red-600 block mt-2 cursor-pointer">Please add at least one image</small>
            )}

            {formData.images && formData.images.length > 0 && (
              <div className="px-3 py-3 bg-blue-50 rounded-md text-sm text-blue-900 cursor-pointer">
                <strong>{formData.images.length}</strong> image{formData.images.length !== 1 ? 's' : ''} added
                {formData.images.length > 1 && ' (will rotate automatically)'}
              </div>
            )}
          </div>
        </div>

        {/* Targeting */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 cursor-pointer">
          <h4 className="text-base font-semibold text-zinc-900 mb-5 pb-3 border-b border-zinc-100 cursor-pointer">
            Targeting
          </h4>

          <div className="mb-5 cursor-pointer">
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.showAlways || false}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    showAlways: e.target.checked,
                    targetKeywords: e.target.checked ? [] : formData.targetKeywords,
                    targetIndexes: e.target.checked ? [] : formData.targetIndexes,
                  })
                }
                className="w-4 h-4 cursor-pointer"
              />
              Show on all searches
            </label>
          </div>

          {!formData.showAlways && (
            <>
              <div className="mb-5 cursor-pointer">
                <label htmlFor="keywords" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                  Target Keywords
                  <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">
                    Show only when these keywords are searched (comma-separated)
                  </small>
                </label>
                <textarea
                  id="keywords"
                  value={formData.keywordsText || formData.targetKeywords?.join(', ') || ''}
                  onChange={(e) => handleKeywordChange(e.target.value)}
                  placeholder="steel, metal, alloy, manufacturing"
                  rows={2}
                  disabled={formData.showAlways}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed cursor-pointer"
                />
                <small className="text-zinc-600 block mt-1 cursor-pointer">
                  {formData.targetKeywords?.length || 0} keywords
                </small>
              </div>

              <div className="mb-5 cursor-pointer">
                <label className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                  Target Content Types
                  <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">
                    Show only on these content types
                  </small>
                </label>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-2 mt-2 p-3 bg-zinc-50 rounded-md max-h-[300px] overflow-y-auto cursor-pointer">
                  {AVAILABLE_INDICES.map((index) => (
                    <label
                      key={index.name}
                      className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.targetIndexes?.includes(index.name) || false}
                        onChange={() => toggleIndex(index.name)}
                        disabled={formData.showAlways}
                        className="w-4 h-4 cursor-pointer"
                      />
                      {index.displayName}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="mb-5 cursor-pointer">
            <label className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
              Target Regions (Optional)
              <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">
                Leave empty to show worldwide, or select specific regions
              </small>
            </label>

            {/* Search box */}
            <input
              type="text"
              value={regionSearchTerm}
              onChange={(e) => setRegionSearchTerm(e.target.value)}
              placeholder="Search regions... (e.g., California, Germany, Ontario)"
              className="w-full px-2 py-2 mb-3 border border-zinc-300 rounded text-sm cursor-pointer"
            />

            <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-2 mt-2 p-3 bg-zinc-50 rounded-md max-h-[300px] overflow-y-auto cursor-pointer">
              {AVAILABLE_REGIONS.filter((region) => {
                // Filter by search term
                if (!regionSearchTerm) return true;
                const searchLower = regionSearchTerm.toLowerCase();
                return (
                  region.name.toLowerCase().includes(searchLower) || region.code.toLowerCase().includes(searchLower)
                );
              }).map((region) => {
                // For "(All)" regions, check if all child regions are selected
                const isParentRegion =
                  region.code === 'US' || region.code === 'CA' || region.code === 'MX' || region.code === 'IN';
                let isChecked = formData.targetRegions?.includes(region.code) || false;

                if (isParentRegion) {
                  const childRegions = AVAILABLE_REGIONS.filter((r) => r.code.startsWith(region.code + '-')).map(
                    (r) => r.code
                  );
                  isChecked =
                    childRegions.length > 0 && childRegions.every((child) => formData.targetRegions?.includes(child));
                }

                return (
                  <label
                    key={region.code}
                    className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleRegion(region.code)}
                      className="w-4 h-4 cursor-pointer"
                    />
                    {region.name}
                  </label>
                );
              })}
            </div>
            {formData.targetRegions && formData.targetRegions.length > 0 && (
              <small className="text-zinc-600 block mt-1 cursor-pointer">
                {formData.targetRegions.length} region(s) selected
              </small>
            )}
          </div>

          <div className="cursor-pointer">
            <label className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
              Search Modes
              <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">
                Select which search mode(s) this ad should appear in
              </small>
            </label>
            <div className="flex gap-4 mt-2 p-3 bg-zinc-50 rounded-md cursor-pointer">
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2">
                <input
                  type="checkbox"
                  checked={
                    !formData.searchModes ||
                    formData.searchModes.length === 0 ||
                    formData.searchModes.includes('regular')
                  }
                  onChange={(e) => {
                    const currentModes = formData.searchModes || [];
                    if (e.target.checked) {
                      // Add regular if not present
                      if (!currentModes.includes('regular')) {
                        setFormData({
                          ...formData,
                          searchModes: [...currentModes, 'regular'] as ('regular' | 'research')[],
                        });
                      }
                    } else {
                      // Remove regular
                      setFormData({
                        ...formData,
                        searchModes: currentModes.filter((m) => m !== 'regular') as ('regular' | 'research')[],
                      });
                    }
                  }}
                  className="w-4 h-4 cursor-pointer"
                />
                General Search
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2">
                <input
                  type="checkbox"
                  checked={
                    !formData.searchModes ||
                    formData.searchModes.length === 0 ||
                    formData.searchModes.includes('research')
                  }
                  onChange={(e) => {
                    const currentModes = formData.searchModes || [];
                    if (e.target.checked) {
                      // Add research if not present
                      if (!currentModes.includes('research')) {
                        setFormData({
                          ...formData,
                          searchModes: [...currentModes, 'research'] as ('regular' | 'research')[],
                        });
                      }
                    } else {
                      // Remove research
                      setFormData({
                        ...formData,
                        searchModes: currentModes.filter((m) => m !== 'research') as ('regular' | 'research')[],
                      });
                    }
                  }}
                  className="w-4 h-4 cursor-pointer"
                />
                Research Mode
              </label>
            </div>
            <small className="text-zinc-600 block mt-1 cursor-pointer">
              {!formData.searchModes || formData.searchModes.length === 0
                ? 'Both modes (default)'
                : formData.searchModes.join(' and ')}
            </small>
          </div>
        </div>

        {/* Scheduling */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6 cursor-pointer">
          <h4 className="text-base font-semibold text-zinc-900 mb-5 pb-3 border-b border-zinc-100 cursor-pointer">
            Scheduling
          </h4>

          <div className="grid grid-cols-2 gap-4 cursor-pointer">
            <div className="mb-5 cursor-pointer">
              <label htmlFor="startDate" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                Start Date <span className="text-red-600 cursor-pointer">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                value={formData.startDate || ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                required
              />
            </div>
            <div className="mb-5 cursor-pointer">
              <label htmlFor="endDate" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                End Date <span className="text-red-600 cursor-pointer">*</span>
              </label>
              <input
                type="date"
                id="endDate"
                value={formData.endDate || ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 cursor-pointer">
            <div className="mb-5 cursor-pointer">
              <label htmlFor="priority" className="block mb-1.5 text-sm font-medium text-zinc-700 cursor-pointer">
                Priority
                <small className="block font-normal text-zinc-500 mt-0.5 cursor-pointer">
                  Higher number = higher priority (1-10)
                </small>
              </label>
              <input
                type="number"
                id="priority"
                value={formData.priority || 1}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })}
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              />
            </div>
            <div className="mb-5 cursor-pointer">
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer py-2 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active || false}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 cursor-pointer"
                />
                Active
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t border-zinc-200 cursor-pointer">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 rounded-lg text-sm font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 hover:border-zinc-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded-lg text-sm font-medium bg-zinc-900 text-white border border-zinc-900 hover:bg-zinc-800 hover:border-zinc-800 transition-colors disabled:bg-zinc-400 disabled:border-zinc-400 disabled:cursor-not-allowed cursor-pointer"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : editingAd ? 'Update Advertisement' : 'Create Advertisement'}
          </button>
        </div>
      </form>

      {/* Image Edit Modal */}
      {editingImageIndex !== null && formData.images && formData.images[editingImageIndex] && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4 cursor-pointer">
          <div
            className="bg-white rounded-lg p-6 max-w-5xl w-full cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 cursor-pointer">
              <h3 className="m-0 text-xl font-semibold cursor-pointer">Edit Image #{editingImageIndex + 1}</h3>
              <button
                type="button"
                onClick={validateAndCloseModal}
                className="bg-transparent border-none text-2xl cursor-pointer text-zinc-500 hover:text-zinc-700 cursor-pointer"
              >
                ×
              </button>
            </div>

            {modalError && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-red-600 mb-4 text-sm cursor-pointer">
                {modalError}
              </div>
            )}

            <div className="grid grid-cols-[300px_1fr] gap-6 cursor-pointer">
              {/* Left Column - Image Preview */}
              <div className="flex flex-col gap-3 cursor-pointer">
                <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 cursor-pointer">
                  <img
                    src={getAbsoluteImageUrl(formData.images[editingImageIndex].url)}
                    alt="Preview"
                    className="max-w-full max-h-[200px] object-contain mx-auto cursor-pointer"
                  />
                </div>

                {/* Alt Text */}
                <div>
                  <label className="block mb-1.5 text-sm font-medium cursor-pointer">Alt Text</label>
                  <input
                    type="text"
                    value={formData.images[editingImageIndex].altText || ''}
                    onChange={(e) => updateImage(editingImageIndex, { altText: e.target.value })}
                    placeholder="Describe the image"
                    className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                  />
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-2 cursor-pointer">
                  <div>
                    <label className="block mb-1.5 text-sm font-medium cursor-pointer">Width (px)</label>
                    <input
                      type="number"
                      value={formData.images[editingImageIndex].width || ''}
                      onChange={(e) =>
                        updateImage(editingImageIndex, { width: parseInt(e.target.value, 10) || undefined })
                      }
                      placeholder="Width"
                      min="1"
                      className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block mb-1.5 text-sm font-medium cursor-pointer">Height (px)</label>
                    <input
                      type="number"
                      value={formData.images[editingImageIndex].height || ''}
                      onChange={(e) =>
                        updateImage(editingImageIndex, { height: parseInt(e.target.value, 10) || undefined })
                      }
                      placeholder="Height"
                      min="1"
                      className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Form Fields */}
              <div className="flex flex-col gap-3 cursor-pointer">
                {/* Display Duration */}
                <div>
                  <label className="block mb-1.5 text-sm font-medium cursor-pointer">
                    Display Duration (seconds) <span className="text-red-600 cursor-pointer">*</span>
                  </label>
                  <input
                    type="number"
                    value={
                      formData.images[editingImageIndex].duration
                        ? formData.images[editingImageIndex].duration! / 1000
                        : ''
                    }
                    onChange={(e) =>
                      updateImage(editingImageIndex, {
                        duration: e.target.value ? parseInt(e.target.value, 10) * 1000 : undefined,
                      })
                    }
                    placeholder="5"
                    min="1"
                    max="120"
                    required
                    className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                  />
                  <small className="text-zinc-500 text-xs cursor-pointer">
                    How long to show this image{' '}
                    {formData.images && formData.images.length > 1 ? 'before rotating to the next' : ''}
                  </small>
                </div>

                {/* Click URL */}
                <div>
                  <label className="block mb-1.5 text-sm font-medium cursor-pointer">
                    Click URL <span className="text-red-600 cursor-pointer">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.images[editingImageIndex].clickUrl || ''}
                    onChange={(e) => updateImage(editingImageIndex, { clickUrl: e.target.value })}
                    placeholder="https://example.com/landing-page"
                    className="w-full px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                  />
                </div>

                {/* UTM Parameters */}
                <div>
                  <label className="block mb-1.5 text-sm font-medium cursor-pointer">UTM Parameters (optional)</label>
                  <div className="grid grid-cols-2 gap-2 cursor-pointer">
                    <input
                      type="text"
                      value={formData.images[editingImageIndex].utmSource || ''}
                      onChange={(e) => updateImage(editingImageIndex, { utmSource: e.target.value })}
                      placeholder="Source"
                      className="px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.images[editingImageIndex].utmMedium || ''}
                      onChange={(e) => updateImage(editingImageIndex, { utmMedium: e.target.value })}
                      placeholder="Medium"
                      className="px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.images[editingImageIndex].utmCampaign || ''}
                      onChange={(e) => updateImage(editingImageIndex, { utmCampaign: e.target.value })}
                      placeholder="Campaign"
                      className="px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.images[editingImageIndex].utmContent || ''}
                      onChange={(e) => updateImage(editingImageIndex, { utmContent: e.target.value })}
                      placeholder="Content"
                      className="px-2 py-1.5 text-sm border border-zinc-300 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Done Button */}
                <button
                  type="button"
                  onClick={validateAndCloseModal}
                  className="w-full px-3 py-2.5 bg-indigo-500 text-white border-none rounded cursor-pointer text-sm font-medium mt-2 hover:bg-indigo-600 cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementForm;
